/**
 * Play.jsx - Public game player component
 * 
 * RETRO ACCENT ZONE: This is a player-facing component where arcade/retro styling
 * is fully allowed and expected. The game frame, game UI, results screen, and
 * all game-related visual elements can use arcade styling including:
 * - Pixel fonts (Share Tech Mono, Press Start 2P, etc.)
 * - Arcade Gold (#F1C40F) and other retro colors
 * - Neon effects, pixel art, and retro UI patterns
 * 
 * This is separate from the admin theme - games should feel fun and arcade-like
 * while the admin dashboard remains clean and professional.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { isCampaignPlayable, normalizeCampaignStatus } from '../data/campaignStatus';
import { decode } from 'js-base64';
import { motion } from 'framer-motion';
import StartEndBackground from './components/StartEndBackground';
import CardFrame from './components/CardFrame';
import { isProceduralBackground, getProceduralDominantColor } from './proceduralBackgrounds';
import { useFont } from '../contexts/FontContext';
import { recordPageView } from '../services/gameAnalytics';

// Error boundary specifically for the game render — catches chunk failures and
// runtime errors inside game components so the player sees a retry screen
// instead of a blank page.
class GameErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
    this._retryTimer = null;
    this._visibilityHandler = null;
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Game render error:', error, info);
    // Auto-retry after 2s (handles transient Firestore/network errors)
    if (this.state.retryCount < 3) {
      this._retryTimer = setTimeout(() => this._retry(), 2000);
    }
  }

  componentDidMount() {
    // When tab becomes visible again, retry if in error state
    this._visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.state.hasError) {
        this._retry();
      }
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  componentWillUnmount() {
    if (this._retryTimer) clearTimeout(this._retryTimer);
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  _retry = () => {
    if (this._retryTimer) clearTimeout(this._retryTimer);
    this.setState((prev) => ({ hasError: false, retryCount: prev.retryCount + 1 }));
  };

  render() {
    if (this.state.hasError) {
      // If we've exhausted auto-retries, show the error screen
      if (this.state.retryCount >= 3) {
        return (
          <div
            className="fixed inset-0 flex items-center justify-center select-none overflow-auto"
            style={{
              background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
              fontFamily: '"Inter", sans-serif',
              minHeight: '100dvh',
              padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
            }}
          >
            <div
              className="text-center px-8 py-10 mx-4 max-w-sm rounded-2xl shadow-xl"
              style={{ background: '#FFFFFF', border: '1px solid rgba(148, 163, 184, 0.2)' }}
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(229, 106, 84, 0.1)' }}>
                <svg className="w-8 h-8" style={{ color: '#E56A54' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2 tracking-tight" style={{ color: '#1E293B', fontFamily: '"Inter", sans-serif' }}>
                Something went wrong
              </h2>
              <p className="text-sm mb-6" style={{ color: '#64748B' }}>
                The game ran into an issue. This usually fixes itself.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="w-full px-5 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:opacity-90"
                style={{ background: '#E56A54' }}
              >
                Reload
              </button>
            </div>
          </div>
        );
      }
      // Auto-retrying — show nothing (avoids flash of error screen)
      return null;
    }
    return this.props.children;
  }
}

// Cache for game components to avoid re-importing
const componentCache = new Map();

// Cache for game data to reduce Firestore calls
const gameDataCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 50; // Maximum number of cached items

// Cache cleanup function
const cleanupCaches = () => {
  const now = Date.now();
  
  // Clean up expired game data
  for (const [key, value] of gameDataCache.entries()) {
    if (now - value.timestamp > CACHE_TTL) {
      gameDataCache.delete(key);
    }
  }
  
  // If cache is still too large, remove oldest entries
  if (gameDataCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(gameDataCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toRemove = entries.slice(0, gameDataCache.size - MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => gameDataCache.delete(key));
  }
  
  // Clean up component cache if it gets too large
  if (componentCache.size > 20) {
    const entries = Array.from(componentCache.entries());
    const toRemove = entries.slice(0, componentCache.size - 20);
    toRemove.forEach(([key]) => componentCache.delete(key));
  }
};

// Run cache cleanup every 5 minutes. Module-scope guard prevents duplicate timers
// if the module is re-evaluated (e.g. during HMR in dev).
if (typeof window !== 'undefined' && !window.__sfCacheCleanupTimer) {
  window.__sfCacheCleanupTimer = setInterval(cleanupCaches, 5 * 60 * 1000);
}

const Play = ({ previewData, isPreview }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { preloadFonts } = useFont();
  const [gameData, setGameData] = useState(previewData);
  const [GameComponent, setGameComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [gameEnded, setGameEnded] = useState(false);
  const [gameEndedReason, setGameEndedReason] = useState(null); // 'draft' | 'completed'
  const [blockedCampaignData, setBlockedCampaignData] = useState(null); // campaign data when blocked (for game-options theme)
  const loadingTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Resolved campaign id — equals the URL :id for doc-id routes, or the id from
  // slugs/{slug} lookup for custom-slug routes. Analytics + leaderboard writes
  // must key on this, not on the raw URL token.
  const [resolvedCampaignId, setResolvedCampaignId] = useState(null);
  
  // Connection monitoring state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnecting, setShowReconnecting] = useState(false);

  // Play viewport: height for layout; width for "phone-like" detection (enforce phone frame when viewport is weird).
  const EDITOR_PHONE_VIEWPORT_HEIGHT = 780; // match Editor.jsx (360 * 19.5/9)
  const EDITOR_PHONE_VIEWPORT_WIDTH = 360;
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
  const [playViewportHeightPx, setPlayViewportHeightPx] = useState(() =>
    isInIframe ? EDITOR_PHONE_VIEWPORT_HEIGHT : null
  );
  const [playViewportWidthPx, setPlayViewportWidthPx] = useState(() =>
    isInIframe ? EDITOR_PHONE_VIEWPORT_WIDTH : null
  );
  useEffect(() => {
    const setViewport = () => {
      if (typeof window === 'undefined') return;
      const w = Math.round(window.innerWidth);
      if (isInIframe) {
        const h = Math.round(window.innerHeight);
        if (h > 0) setPlayViewportHeightPx(h);
        if (w > 0) setPlayViewportWidthPx(w);
        return;
      }
      if (w > 0) setPlayViewportWidthPx(w);
      const vv = window.visualViewport;
      if (vv && typeof vv.height === 'number') {
        setPlayViewportHeightPx(Math.round(vv.height));
      } else {
        setPlayViewportHeightPx(null);
      }
    };
    if (isInIframe) setViewport();
    if (isInIframe) {
      let rafId2;
      const rafId1 = requestAnimationFrame(() => {
        setViewport();
        rafId2 = requestAnimationFrame(setViewport);
      });
      const tId = setTimeout(setViewport, 150);
      const ro = new ResizeObserver(setViewport);
      ro.observe(document.documentElement);
      return () => {
        cancelAnimationFrame(rafId1);
        if (rafId2 != null) cancelAnimationFrame(rafId2);
        clearTimeout(tId);
        ro.disconnect();
      };
    }
    setViewport();
    let orientationTimeoutId = null;
    const onOrientation = () => {
      if (orientationTimeoutId) clearTimeout(orientationTimeoutId);
      orientationTimeoutId = setTimeout(setViewport, 150);
    };
    window.visualViewport?.addEventListener('resize', setViewport);
    window.visualViewport?.addEventListener('scroll', setViewport);
    window.addEventListener('resize', setViewport);
    window.addEventListener('orientationchange', onOrientation);
    return () => {
      if (orientationTimeoutId) clearTimeout(orientationTimeoutId);
      window.visualViewport?.removeEventListener('resize', setViewport);
      window.visualViewport?.removeEventListener('scroll', setViewport);
      window.removeEventListener('resize', setViewport);
      window.removeEventListener('orientationchange', onOrientation);
    };
  }, [isInIframe]);
  // Lock body scroll, prevent text selection, extend to full viewport (no gray bars)
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyTouchAction = document.body.style.touchAction;
    const prevBodyMargin = document.body.style.margin;
    const prevHtmlMargin = document.documentElement.style.margin;
    const prevBodyPadding = document.body.style.padding;
    const prevHtmlPadding = document.documentElement.style.padding;
    const prevHtmlFontSize = document.documentElement.style.fontSize;
    const prevBodyFontSize = document.body.style.fontSize;
    const prevHtmlTextSizeAdjust = document.documentElement.style.webkitTextSizeAdjust;
    const prevBodyTextSizeAdjust = document.body.style.webkitTextSizeAdjust;
    const prevHtmlMozTextSizeAdjust = document.documentElement.style.textSizeAdjust;
    const prevBodyMozTextSizeAdjust = document.body.style.textSizeAdjust;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    // Scale root font with viewport so game UI scales on different devices. fontScale (from options) applied in a separate effect.
    document.documentElement.style.fontSize = 'clamp(11px, 2.2vw + 6px, 16px)';
    document.body.style.fontSize = 'inherit';
    document.documentElement.style.webkitTextSizeAdjust = '100%';
    document.body.style.webkitTextSizeAdjust = '100%';
    document.documentElement.style.textSizeAdjust = '100%';
    document.body.style.textSizeAdjust = '100%';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.touchAction = prevBodyTouchAction;
      document.body.style.margin = prevBodyMargin;
      document.body.style.padding = prevBodyPadding;
      document.documentElement.style.margin = prevHtmlMargin;
      document.documentElement.style.padding = prevHtmlPadding;
      document.documentElement.style.fontSize = prevHtmlFontSize;
      document.body.style.fontSize = prevBodyFontSize;
      document.documentElement.style.webkitTextSizeAdjust = prevHtmlTextSizeAdjust;
      document.body.style.webkitTextSizeAdjust = prevBodyTextSizeAdjust;
      document.documentElement.style.textSizeAdjust = prevHtmlMozTextSizeAdjust;
      document.body.style.textSizeAdjust = prevBodyMozTextSizeAdjust;
    };
  }, []);

  // Apply font scale from options (scales all rem-based game UI while keeping viewport responsiveness)
  const fontScale = gameData?.options?.fontScale;
  useEffect(() => {
    const base = 'clamp(11px, 2.2vw + 6px, 16px)';
    const scale = fontScale != null && fontScale !== 1 ? fontScale : 1;
    document.documentElement.style.fontSize = scale === 1 ? base : `calc(${base} * ${scale})`;
  }, [fontScale]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // === LIVE DISPLAY HARDENING ===
  
  // Connection status monitoring with reconnect indicator
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Connection restored');
      setIsOnline(true);
      setShowReconnecting(false);
      // Auto-retry if there was an error
      if (error?.canRetry) {
        setError(null);
        setRetryCount(prev => prev + 1);
      }
    };
    
    const handleOffline = () => {
      console.log('📴 Connection lost');
      setIsOnline(false);
      setShowReconnecting(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [error]);


  // Record page view once on mount (tracks QR-scan-to-play conversion)
  const pageViewRecordedRef = useRef(false);
  useEffect(() => {
    if (pageViewRecordedRef.current) return;
    if (!resolvedCampaignId || isPreview) return;
    // Skip encoded editor previews and ?preview=true test plays
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview')) return;
    pageViewRecordedRef.current = true;
    recordPageView(resolvedCampaignId);
  }, [resolvedCampaignId, isPreview]);

  // Set the browser-tab title from the campaign once gameData is loaded.
  // App.js's initializeDynamicSEO() runs once on app mount with the generic
  // marketing title; this effect overrides it for the play route so a shared
  // link shows the campaign title. The customer's brand owns the tab — no
  // SmilesFlow suffix on player-facing pages.
  useEffect(() => {
    const campaignTitle = gameData?.options?.title;
    if (!campaignTitle) return;
    const previous = document.title;
    document.title = campaignTitle;
    return () => {
      document.title = previous;
    };
  }, [gameData?.options?.title]);

  // Enhanced error handling with user-friendly messages
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    
    let userMessage = 'Something went wrong loading the game.';
    let canRetry = true;
    
    if (error.name === 'AbortError') {
      userMessage = 'Loading was cancelled.';
    } else if (error.message?.includes('timeout') || error.message?.includes('network')) {
      userMessage = 'Connection issue. Tap retry or switch tabs and come back.';
    } else if (error.message?.includes('not found') || error.message?.includes('404')) {
      userMessage = 'Game not found. The link might be invalid or the game may have been removed.';
      canRetry = false;
    } else if (error.message?.includes('permission') || error.message?.includes('403')) {
      userMessage = 'You don\'t have permission to access this game.';
      canRetry = false;
    } else if (error.code === 'unavailable') {
      userMessage = 'Game service is temporarily unavailable. Will auto-retry when you return.';
    }
    
    setError({ message: userMessage, canRetry });
  }, []);

  // Retry mechanism
  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryCount(prev => prev + 1);
  }, []);

  // Auto-retry when tab becomes visible again (handles mobile tab suspension)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && error?.canRetry) {
        console.log('Tab became visible with retryable error - auto-retrying...');
        retry();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [error, retry]);

  // Load game component with caching and timeout
  const loadGameComponent = useCallback(async (gameType) => {
    if (!gameType) return null;

    // Check cache first
    const cacheKey = gameType;
    if (componentCache.has(cacheKey)) {
      return componentCache.get(cacheKey);
    }

    try {
      // Set timeout for component loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Component loading timeout')), 10000);
      });

      const importPromise = import(`../games/${gameType}/Main`);

      const module = await Promise.race([importPromise, timeoutPromise]);

      // Use the default export directly instead of wrapping in React.lazy.
      // The module is already resolved so React.lazy adds an unnecessary
      // Suspense cycle that can produce blank screens on mobile when the
      // chunk is already cached.
      const component = module.default || module;

      // Cache the component
      componentCache.set(cacheKey, component);
      return component;
    } catch (error) {
      console.error(`Failed to load game component for ${gameType}:`, error);
      throw new Error(`Failed to load game component: ${error.message}`);
    }
  }, []);

  // Load game data with caching and timeout.
  // Supports either a Firestore doc id or a custom slug. Slug shape ([a-z0-9-], 3–40,
  // contains a hyphen or lowercase). Firestore ids are 20 chars mixed-case alphanumeric,
  // so we only hit the slugs collection when the URL token doesn't look like a doc id.
  const loadGameData = useCallback(async () => {
    if (!id) return null;

    const looksLikeDocId = /^[A-Za-z0-9]{20}$/.test(id);

    // Check cache first (keyed on URL token — same token always resolves to same game)
    const cacheKey = `game_${id}`;
    const cached = gameDataCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      console.log(`Cache hit for game ID: ${id}`);
      return { data: cached.data, campaignId: cached.campaignId };
    }

    abortControllerRef.current = new AbortController();

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Database timeout')), 8000);
      });

      let campaignId = id;
      let docSnap = null;

      if (looksLikeDocId) {
        // Doc-id path (fast). If the doc doesn't exist, fall back to slug lookup —
        // handles the rare case where a slug happens to match the 20-char id shape.
        docSnap = await Promise.race([getDoc(doc(db, 'campaign', id)), timeoutPromise]);
        if (abortControllerRef.current?.signal.aborted) {
          throw new Error('Request was cancelled');
        }
        if (!docSnap.exists()) {
          const slugSnap = await Promise.race([getDoc(doc(db, 'slugs', id.toLowerCase())), timeoutPromise]);
          if (slugSnap.exists()) {
            campaignId = slugSnap.data().campaignId;
            if (campaignId) {
              docSnap = await Promise.race([getDoc(doc(db, 'campaign', campaignId)), timeoutPromise]);
            }
          }
        }
      } else {
        // Slug path: resolve slug → campaignId via slugs/{slug}
        const slugSnap = await Promise.race([getDoc(doc(db, 'slugs', id.toLowerCase())), timeoutPromise]);
        if (abortControllerRef.current?.signal.aborted) {
          throw new Error('Request was cancelled');
        }
        if (!slugSnap.exists()) {
          throw new Error('Game not found');
        }
        campaignId = slugSnap.data().campaignId;
        if (!campaignId) throw new Error('Game not found');
        docSnap = await Promise.race([getDoc(doc(db, 'campaign', campaignId)), timeoutPromise]);
      }

      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Request was cancelled');
      }

      if (!docSnap || !docSnap.exists()) {
        throw new Error('Game not found');
      }

      const data = docSnap.data();

      gameDataCache.set(cacheKey, { data, campaignId, timestamp: Date.now() });
      console.log(`Cached game data for ID: ${id}`);

      return { data, campaignId };
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'Request was cancelled') {
        throw new Error('Request was cancelled');
      }
      throw error;
    }
  }, [id]);

  // Main loading effect
  useEffect(() => {
    let cancelled = false; // Prevent timeout race: if load succeeds, ignore late timeout

    const loadGame = async () => {
      // Set overall timeout for the entire loading process
      loadingTimeoutRef.current = setTimeout(() => {
        if (!cancelled) {
          handleError(new Error('Loading timeout'), 'overall timeout');
          setLoading(false);
        }
      }, 15000); // 15 second overall timeout

      try {
        let data = previewData;
        let campaignIdFromLoad = null;

        const unwrapLoad = (res) => {
          if (res && typeof res === 'object' && 'data' in res) {
            campaignIdFromLoad = res.campaignId || null;
            return res.data;
          }
          return res;
        };

        let fromEncodedPreview = false;
        if (!isPreview) {
          // Check URL for preview data first (editor encoded state or ?preview=true)
          const urlParams = new URLSearchParams(window.location.search);
          const previewParam = urlParams.get('preview');

          if (previewParam) {
            if (previewParam === 'true') {
              // Test play / dashboard preview: load from DB, previewMode set below
              data = unwrapLoad(await loadGameData());
            } else {
              try {
                data = JSON.parse(decode(previewParam));
                fromEncodedPreview = true; // Editor preview: no analytics/leaderboard
              } catch (decodeError) {
                console.error('Error decoding preview data:', decodeError);
                data = unwrapLoad(await loadGameData());
              }
            }
          } else {
            // Load from database
            data = unwrapLoad(await loadGameData());
          }
        }

        if (cancelled) return;

        if (!data) {
          throw new Error('No game data found');
        }

        if (!data.gameType) {
          throw new Error('Game type not specified');
        }

        // Start component import immediately — runs in parallel with the
        // synchronous checks and state setup below so the bundle downloads
        // while we do bookkeeping.
        const componentPromise = loadGameComponent(data.gameType);

        // Block play when campaign is not playable (only published is playable)
        const urlParamsForStatus = new URLSearchParams(window.location.search);
        const isPreviewMode = urlParamsForStatus.get('preview') === 'true';
        if (!isPreview && !isPreviewMode && !fromEncodedPreview && !isCampaignPlayable(data.status)) {
          setBlockedCampaignData(data);
          setGameEnded(true);
          const normalizedStatus = normalizeCampaignStatus(data.status);
          setGameEndedReason(normalizedStatus); // 'draft', 'paused', or 'completed'
          setLoading(false);
          cleanup();
          return;
        }

        // Add the ID; keep options as-is (cardOverlayEffect is attached to card style, derived from frame)
        // Use the resolved campaign id (from slug lookup or direct doc-id route) so
        // leaderboard/analytics writes always key on the campaign, never on a slug.
        const effectiveCampaignId = campaignIdFromLoad || id || previewData?.id || 'preview';
        const gameDataWithId = {
          ...data,
          id: effectiveCampaignId
        };
        if (campaignIdFromLoad) setResolvedCampaignId(campaignIdFromLoad);

        // Preview mode: no analytics or leaderboard writes (?preview=true or editor encoded preview)
        if (urlParamsForStatus.get('preview') === 'true' || fromEncodedPreview) {
          gameDataWithId.previewMode = true;
        }

        // Set game data
        setGameData(gameDataWithId);

        // Await the component (likely already resolved if cached or fast network)
        const component = await componentPromise;
        if (cancelled) return;
        setGameComponent(() => component);

        // Mark as successfully loaded — cancel the timeout
        cancelled = true;
        // Clear any previous errors
        setError(null);

      } catch (error) {
        if (!cancelled) {
          handleError(error, 'loadGame');
        }
      } finally {
        cleanup();
        if (!cancelled) {
          setLoading(false);
        } else {
          // Successful load: ensure loading is cleared after state is set
          setLoading(false);
        }
      }
    };

    // Reset state when dependencies change
    setLoading(true);
    setError(null);
    setGameEnded(false);
    setGameEndedReason(null);
    setBlockedCampaignData(null);

    loadGame();

    // Cleanup on unmount or dependency change
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [previewData, isPreview, id, retryCount, loadGameData, loadGameComponent, handleError, cleanup]);

  // Sync mobile browser chrome color with the game's background so the
  // status bar / bottom bar blend seamlessly instead of showing a jarring
  // default color.
  useEffect(() => {
    if (!gameData) return;
    const opts = gameData.options || {};
    const bgImg = opts.backgroundImage || opts.gameBackgroundImage;
    let chromeColor = null;

    if (bgImg && isProceduralBackground(bgImg)) {
      chromeColor = getProceduralDominantColor(bgImg);
    } else if (bgImg) {
      // Static image backgrounds — use black to match the fallback
      chromeColor = '#000';
    }
    // Fall back to the campaign's custom background color
    if (!chromeColor) {
      chromeColor = opts.customColors?.background || '#87CEEB';
    }

    const meta = document.querySelector('meta[name="theme-color"]');
    const prevColor = meta?.getAttribute('content');
    if (meta) meta.setAttribute('content', chromeColor);
    document.body.style.backgroundColor = chromeColor;

    return () => {
      // Restore defaults on unmount
      if (meta) meta.setAttribute('content', prevColor || '#1a1a1a');
      document.body.style.backgroundColor = '';
    };
  }, [gameData]);

  // Preload theme font for blocked campaign screens (completed/paused/draft)
  useEffect(() => {
    const data = blockedCampaignData || gameData;
    if (!gameEnded || !data) return;
    const font = data.options?.theme?.typography?.primaryFont || data.options?.font;
    if (font && font !== 'Inter') preloadFonts([font]);
  }, [gameEnded, blockedCampaignData, gameData, preloadFonts]);

  // Message listener for preview updates (editor iframe: keep preview mode)
  useEffect(() => {
    const trustedOrigin = window.location.origin;
    const handleMessage = (event) => {
      if (event.origin !== trustedOrigin) return;
      if (window.parent && event.source !== window.parent) return;
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'PREVIEW_UPDATE') {
        const incoming = event.data.gameData || {};
        setGameData(prev => ({
          ...(prev || {}),
          ...incoming,
          id: id || 'preview',
          previewMode: true,
          options: { ...(incoming.options || {}), cardOverlayEffect: incoming.options?.cardOverlayEffect }
        }));
      }

      if (event.data.type === 'COMPANY_UPDATE') {
        const incomingCompany = event.data.companyData || {};
        setGameData((prev) => ({
          ...(prev || {}),
          id: (prev && prev.id) || id || 'preview',
          previewMode: true,
          companyData: incomingCompany,
        }));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [id]);

  // DEBUG: Force error screen with ?testerror=1 or ?testerror=2
  const testError = useMemo(
    () => new URLSearchParams(window.location.search).get('testerror'),
    []
  );
  if (testError === '1') {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center select-none overflow-auto"
        style={{
          background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
          fontFamily: '"Inter", sans-serif',
          minHeight: '100dvh',
          padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
        }}
      >
        <div 
          className="text-center px-8 py-10 mx-4 max-w-sm rounded-2xl shadow-xl"
          style={{
            background: '#FFFFFF',
            border: '1px solid rgba(148, 163, 184, 0.2)'
          }}
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(229, 106, 84, 0.1)' }}>
            <svg className="w-8 h-8" style={{ color: '#E56A54' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 tracking-tight" style={{ color: '#1E293B', fontFamily: '"Inter", sans-serif' }}>
            Connection Issue
          </h2>
          <p className="text-sm mb-2" style={{ color: '#64748B' }}>
            We couldn't load the game. This usually fixes itself.
          </p>
          <p className="text-xs mb-6" style={{ color: '#94A3B8' }}>
            Will auto-retry when you return to this tab
          </p>
          <button
            onClick={retry}
            className="w-full px-5 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:opacity-90"
            style={{ background: '#F4B942' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  if (testError === '2') {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center select-none overflow-auto"
        style={{
          background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
          fontFamily: '"Inter", sans-serif',
          minHeight: '100dvh',
          padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
        }}
      >
        <div 
          className="text-center px-8 py-10 mx-4 max-w-sm rounded-2xl shadow-xl"
          style={{
            background: '#FFFFFF',
            border: '1px solid rgba(148, 163, 184, 0.2)'
          }}
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 tracking-tight" style={{ color: '#1E293B', fontFamily: '"Inter", sans-serif' }}>
            Game Not Found
          </h2>
          <p className="text-sm mb-6" style={{ color: '#64748B' }}>
            This game link is invalid or the game has been removed. Please check the link and try again.
          </p>
        </div>
      </div>
    );
  }

  // Campaign not available (draft, paused, or completed)  -  same card style as Start/End card
  if (gameEnded) {
    const themeData = blockedCampaignData || gameData;
    const opts = themeData?.options || {};
    const colors = {
      background: opts.customColors?.background || '#000000',
      surface: opts.customColors?.surface || '#FFFFFF',
      primary: opts.customColors?.primary || '#000000',
      text: opts.customColors?.text || '#6B7280',
      primaryAction: opts.customColors?.primaryAction || '#7C3AED',
      secondaryAction: opts.customColors?.secondaryAction || '#F97316',
      buttonText: opts.customColors?.buttonText || '#FFFFFF'
    };
    const frameStyle = opts.frameStyle || 'clean';
    const primaryFont = opts.theme?.typography?.primaryFont || opts.font || 'Inter';
    const fontFamily = primaryFont ? `"${primaryFont}", sans-serif` : 'inherit';
    const backgroundColor = opts.customColors?.background || '#87CEEB';
    const backgroundImage = opts.backgroundImage || null;

    const getBlockedMessage = () => {
      switch (gameEndedReason) {
        case 'draft':
          return {
            title: 'Campaign not yet published',
            message: 'This campaign is not yet published. The organizer will share the link when it\'s ready.'
          };
        case 'paused':
          return {
            title: 'This game isn\'t available right now',
            message: 'The organizer has temporarily paused this campaign. Check back later.'
          };
        case 'completed':
          return {
            title: 'This campaign has ended',
            message: 'The organizer has marked this campaign as completed. It is no longer accepting new plays.'
          };
        default:
          return {
            title: 'Game unavailable',
            message: 'This game is not currently available.'
          };
      }
    };
    const blockedMsg = getBlockedMessage();

    return (
      <div className="absolute inset-0 flex items-center justify-center select-none overflow-hidden" style={{ fontFamily }}>
        <StartEndBackground backgroundImage={backgroundImage} backgroundColor={backgroundColor} />
        <div className="relative z-10 w-full min-w-0 max-w-[420px] px-4">
          <CardFrame frameStyle={frameStyle} colors={colors} animate={true} exitWithParent={false}>
            <div className="text-center px-8 py-10">
              <h2 className="text-xl font-semibold mb-2 tracking-tight" style={{ color: colors.text, fontFamily }}>
                {blockedMsg.title}
              </h2>
              <p className="text-sm" style={{ color: colors.text }}>
                {blockedMsg.message}
              </p>
            </div>
          </CardFrame>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center select-none overflow-hidden"
        style={{
          background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
          fontFamily: '"Inter", sans-serif'
        }}
        role="status"
        aria-label="Loading game"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ opacity: { duration: 0.3 }, scale: { duration: 0.3 } }}
          className="w-8 h-8 rounded-full border-4 animate-spin"
          style={{ borderColor: 'rgba(244, 185, 66, 0.25)', borderTopColor: '#F4B942' }}
          aria-hidden="true"
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center select-none overflow-auto"
        style={{
          background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
          fontFamily: '"Inter", sans-serif',
          minHeight: '100dvh',
          padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
        }}
      >
        <div 
          className="text-center px-8 py-10 mx-4 max-w-sm rounded-2xl shadow-xl"
          style={{
            background: '#FFFFFF',
            border: '1px solid rgba(148, 163, 184, 0.2)'
          }}
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(229, 106, 84, 0.1)' }}>
            <svg className="w-8 h-8" style={{ color: '#E56A54' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2 tracking-tight" style={{ color: '#1E293B', fontFamily: '"Inter", sans-serif' }}>
            {error.canRetry ? 'Connection Issue' : 'Something Went Wrong'}
          </h2>
          <p className="text-sm mb-2" style={{ color: '#64748B' }}>
            {error.message}
          </p>
          {error.canRetry && (
            <p className="text-xs mb-6" style={{ color: '#94A3B8' }}>
              Will auto-retry when you return to this tab
            </p>
          )}
          <button
            onClick={error.canRetry ? retry : () => window.history.back()}
            className="w-full px-5 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:opacity-90 mt-6"
            style={{ background: '#E56A54' }}
          >
            {error.canRetry ? 'Try Again' : 'Go Back'}
          </button>
        </div>
      </div>
    );
  }

  // Still resolving (state updates may not batch perfectly — show spinner, not "not found")
  if (!gameData || !GameComponent) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center select-none overflow-hidden"
        style={{
          background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
          fontFamily: '"Inter", sans-serif'
        }}
        role="status"
        aria-label="Loading game"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ opacity: { duration: 0.3 }, scale: { duration: 0.3 } }}
          className="w-8 h-8 rounded-full border-4 animate-spin"
          style={{ borderColor: 'rgba(244, 185, 66, 0.25)', borderTopColor: '#F4B942' }}
          aria-hidden="true"
        />
      </div>
    );
  }

  // Successful game render
  return (
    <GameErrorBoundary>
      <div
        className="play-viewport-fill fixed inset-0 flex select-none items-center justify-center"
        style={{
          fontFamily: gameData?.options?.font || 'inherit',
          overflow: 'hidden',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          ...(playViewportHeightPx != null
            ? { height: playViewportHeightPx, minHeight: playViewportHeightPx, maxHeight: playViewportHeightPx }
            : { height: '100dvh', minHeight: '100dvh', maxHeight: '100dvh' }),
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
          overscrollBehavior: 'none',
          background: (gameData?.options?.backgroundImage || gameData?.options?.gameBackgroundImage) ? '#000' : (gameData?.options?.customColors?.background || '#87CEEB'),
        }}
      >
        <div className="play-game-inner relative w-full h-full min-w-0 min-h-0">
          <GameComponent gameData={gameData} />
          {(isPreview || gameData?.previewMode) && (() => {
            // Hide the Exit preview button when this page is loaded inside
            // an iframe (i.e. the editor's live preview). The editor has
            // its own controls — adding "Exit preview" inside the iframe
            // is just visual noise. We still show it when a tester opens
            // /p/{id}?preview=true in their own tab.
            try {
              if (typeof window !== 'undefined' && window.self !== window.top) return null;
            } catch {
              // Cross-origin frame access throws — assume we're in an iframe.
              return null;
            }
            return (
            <button
              type="button"
              onClick={() => {
                const campaignId = id || gameData?.id;
                if (campaignId) {
                  navigate(`/studio/campaign/${campaignId}`);
                } else {
                  navigate('/studio/dashboard');
                }
              }}
              className="text-sm md:text-base font-semibold"
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 9999,
                padding: '8px 14px',
                background: 'rgba(0, 0, 0, 0.78)',
                color: '#fff',
                borderRadius: '8px',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
              aria-label="Go to campaign"
            >
              <span>Go to campaign</span>
              <span aria-hidden style={{ opacity: 0.7 }}>→</span>
            </button>
            );
          })()}
        </div>
      </div>
    </GameErrorBoundary>
  );
};

export default Play;
